import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, eur, API, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import { Download, ArrowLeft, Package } from "lucide-react";

const STEPS = [
  { key: "ricevuto", label: "Ricevuto" },
  { key: "in_lavorazione", label: "In lavorazione" },
  { key: "pronto", label: "Pronto" },
  { key: "spedito", label: "Spedito" },
  { key: "consegnato", label: "Consegnato" },
];

function stepIndex(status) {
  const i = STEPS.findIndex((s) => s.key === status);
  return i < 0 ? 0 : i;
}

export function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (user === false) { nav("/accedi?redirect=/ordini"); return; }
    if (user && user.id) {
      api.get("/orders/mine").then((r) => setOrders(r.data)).finally(() => setLoading(false));
    }
  }, [user, nav]);

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="my-orders-page">
      <div className="overline text-[#B97A48] mb-3">Area clienti</div>
      <h1 className="font-serif text-5xl leading-tight">I miei ordini</h1>

      {loading ? (
        <div className="mt-12 text-[#5C5A56]">Caricamento…</div>
      ) : orders.length === 0 ? (
        <div className="mt-16 text-center py-20 border border-dashed border-[#E4DFD5]">
          <Package size={36} strokeWidth={1} className="mx-auto text-[#5C5A56]" />
          <p className="mt-4 text-[#5C5A56]">Non hai ancora effettuato ordini.</p>
          <Link to="/servizi" className="btn-primary mt-6">Prenota un servizio</Link>
        </div>
      ) : (
        <div className="mt-12 space-y-4">
          {orders.map((o) => (
            <Link to={`/ordini/${o.id}`} key={o.id} className="block card-luxury p-6 md:p-8" data-testid={`order-row-${o.id}`}>
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <div className="overline mb-1">Ordine #{o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="font-serif text-xl">{o.items.map((i) => i.name).join(", ")}</div>
                  <div className="text-xs text-[#5C5A56] mt-1">{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.order_type === "service" ? "Servizio" : "Prodotti"} · Tracking: {o.tracking_number}</div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <div className="font-serif text-2xl text-[#B97A48]">{eur(o.total)}</div>
                  <div className="flex gap-2">
                    <StatusBadge status={o.payment_status} />
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (user === false) { nav(`/accedi?redirect=/ordini/${id}`); return; }
    if (user && user.id) {
      api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch((e) => setErr(formatError(e)));
    }
  }, [id, user, nav]);

  const downloadLabel = async () => {
    try {
      const token = localStorage.getItem("psc_token");
      const res = await fetch(`${API}/orders/${id}/label`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || "Etichetta non ancora disponibile");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = order.label_filename || `etichetta-${order.tracking_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Errore nel download dell'etichetta");
    }
  };

  const retryPayment = async () => {
    try {
      const { data } = await api.post("/payments/checkout", { order_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setErr(formatError(e)); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#9E413B]">{err}</div>;
  if (!order) return <div className="max-w-7xl mx-auto px-6 py-20">Caricamento…</div>;

  const sIdx = stepIndex(order.status);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="order-detail-page">
      <Link to="/ordini" className="text-sm text-[#5C5A56] hover:text-[#1C1B1A] inline-flex items-center gap-1 mb-8"><ArrowLeft size={14} /> I miei ordini</Link>

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="overline text-[#B97A48] mb-2">Ordine #{order.id.slice(0, 8).toUpperCase()}</div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight">Dettaglio ordine</h1>
          <div className="text-sm text-[#5C5A56] mt-2">Creato il {new Date(order.created_at).toLocaleString("it-IT")}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={order.payment_status} />
          <StatusBadge status={order.status} />
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-12 grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2">
          <div className="overline mb-5">Stato della lavorazione</div>
          <div>
            {STEPS.map((s, i) => (
              <div key={s.key} className={`timeline-step ${i < sIdx ? "done" : ""} ${i === sIdx ? "active" : ""}`} data-testid={`timeline-${s.key}`}>
                <div className="font-medium">{s.label}</div>
                <div className="text-xs text-[#5C5A56]">
                  {i < sIdx ? "Completato" : i === sIdx ? "In corso" : "In attesa"}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <div className="overline mb-4">Articoli</div>
            <div className="border border-[#E4DFD5] divide-y divide-[#E4DFD5]">
              {order.items.map((it, idx) => (
                <div key={idx} className="p-4 flex justify-between text-sm">
                  <div>
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-[#5C5A56] capitalize">{it.item_type} × {it.quantity}</div>
                  </div>
                  <div className="font-serif text-lg">{eur(it.price * it.quantity)}</div>
                </div>
              ))}
            </div>
          </div>

          {order.shoe_info && (
            <div className="mt-10">
              <div className="overline mb-3">Dati calzature</div>
              <div className="grid grid-cols-2 gap-3 text-sm p-5 bg-[#F4F1EA] border border-[#E4DFD5]">
                <div><span className="text-[#5C5A56]">Marca:</span> {order.shoe_info.brand || "—"}</div>
                <div><span className="text-[#5C5A56]">Modello:</span> {order.shoe_info.model || "—"}</div>
                <div><span className="text-[#5C5A56]">Colore:</span> {order.shoe_info.color || "—"}</div>
                <div><span className="text-[#5C5A56]">Materiale:</span> {order.shoe_info.material || "—"}</div>
                {order.shoe_info.notes && <div className="col-span-2"><span className="text-[#5C5A56]">Note:</span> {order.shoe_info.notes}</div>}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="card-luxury p-6 sticky top-24">
            <div className="overline mb-4">Riepilogo</div>
            <div className="flex justify-between text-sm py-1"><span>Subtotale</span><span>{eur(order.subtotal)}</span></div>
            <div className="flex justify-between text-sm py-1"><span>Spedizione</span><span>{order.shipping === 0 ? "Gratis" : eur(order.shipping)}</span></div>
            <div className="flex justify-between text-lg font-serif pt-3 mt-3 border-t border-[#E4DFD5]">
              <span>Totale</span><span className="text-[#B97A48]">{eur(order.total)}</span>
            </div>

            <div className="mt-6 space-y-2">
              <div className="overline text-[0.65rem]">Tracking spedizione</div>
              <div className="font-mono text-sm">{order.tracking_number}</div>
              {order.has_label ? (
                <button onClick={downloadLabel} className="btn-primary w-full justify-center mt-2" data-testid="download-label-btn">
                  <Download size={14} /> Scarica etichetta
                </button>
              ) : (
                <div className="mt-3 p-4 bg-[#F4F1EA] border border-[#E4DFD5] text-xs text-[#5C5A56] leading-relaxed" data-testid="label-pending">
                  <div className="font-medium text-[#1C1B1A] mb-1">Etichetta in preparazione</div>
                  La tua etichetta di spedizione prepagata (SDA o DHL) sarà disponibile qui a breve.
                  Riceverai una notifica via email appena pronta.
                </div>
              )}
            </div>

            {order.payment_status !== "paid" && (
              <button onClick={retryPayment} className="btn-accent w-full justify-center mt-4" data-testid="retry-payment-btn">
                Completa pagamento
              </button>
            )}

            <div className="mt-6 pt-6 border-t border-[#E4DFD5] text-xs text-[#5C5A56]">
              <div className="font-medium text-[#1C1B1A] mb-1">Spedizione a</div>
              <div>{order.shipping_address?.full_name}</div>
              <div>{order.shipping_address?.street}</div>
              <div>{order.shipping_address?.zip} {order.shipping_address?.city} ({order.shipping_address?.province})</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
